import React from 'react'
import memoize from 'memoize-one'
import theme from 'theme'

import { LinearProgress, IconButton, Tooltip } from '@material-ui/core'
import {
  TuneTwoTone,
  TableChartTwoTone,
  TimelineTwoTone,
  Info,
  TrendingUpTwoTone,
  ChromeReaderModeTwoTone,
} from '@material-ui/icons'
import { BreakpointValues } from '@material-ui/core/styles/createBreakpoints'

import Sensor from './Sensor'
import MeasuresTable from '../details/MeasuresTable'
import { ModelState } from '../../store/ModelState'
import SensorChart, { Reference } from './SensorChart'
import {
  TelemetryType,
  TelemetryClient,
  TankLevelBalanceResponse,
  ProductionCode,
} from '../../api/apiservice'
import { TelemetryMessage } from '../../store/SiteDetails'
import { AxisDomain } from 'recharts'
import { multitenantApiBasePath, getFetch } from '../../api/fetch'
import { HistoricalDataEntry, getTankLevelBalance } from '../../api/client'
import { TimeRange } from '../misc/TimeRange'
import { Asset, Site } from 'store/Site'
import { HistoricalDataByTelemetryType } from 'store/HistoricalData'
import {
  StatusRangeResponse,
  TelemetryStatus,
  ThresholdsType,
} from 'api/alertservice'
import SensorChartRates from './SensorChartRates'
import { celsiusToFahrenheit, inchToFeet } from 'components/utils/converter'

export interface Reading {
  readonly value?: number
  readonly rawValue?: number
  readonly formattedValue: React.ReactNode
  readonly formattedRawValue: React.ReactNode
  readonly timestamp?: Date
  readonly unitOfMeasure?: string
}

export interface SensorWithChartProps extends ModelState {
  readonly accessToken: string
  readonly tenantId: number
  readonly realtimeData: TelemetryMessage[]
  readonly timeRange: TimeRange
  readonly param?: any
  readonly thresholds: StatusRangeResponse[]
  readonly site: Site
  readonly asset: Asset
  readonly fullWidthChart?: boolean
  readonly historicalData: HistoricalDataByTelemetryType
  readonly initialLatestValue?: TelemetryMessage
  readonly thresholdType: ThresholdsType
}

interface ComponentState<T> extends ModelState {
  readonly gaugeReading: Reading
  readonly subState?: T
  readonly references?: Reference[]
  readonly referenceName?: string
  readonly isTableViewSelected: boolean
  readonly isChartViewSelected: boolean
  readonly isChartRateViewSelected: boolean
  readonly rawValue?: number
  readonly tankBalance?: TankLevelBalanceResponse
  readonly hiddenData: string[]
  readonly modalConfirmation: boolean
  readonly showSnackbar: boolean
  readonly snackbarMessage: string
  readonly snackbarSeverity: 'success' | 'error' | 'warning' | 'info'
  readonly user: any
  readonly pumpState: 'On' | 'Pending' | 'Off'
  readonly startHourBPD?: string
  readonly endHourBPD?: string
}

interface Props {
  readonly onOpenThresholds?: (TelemetryType, assetId, assetName) => void
  readonly onOpenRateOfChangeThresholds?: (
    TelemetryType,
    assetId,
    assetName
  ) => void
  readonly onOpenAssetInfo?: (TelemetryType, asset, site) => void
  readonly hideGaugeBreakpoint?: keyof BreakpointValues
}

let previousStateProp: string[]

export type AllProps = Props & SensorWithChartProps

export default abstract class SensorWithChart<
  TSubState = {}
> extends React.Component<AllProps, ComponentState<TSubState>> {
  protected readonly TelemetryType: TelemetryType
  protected readonly Title: string
  protected readonly YAxisDomain?: [AxisDomain, AxisDomain]

  protected readonly memoizedData = memoize(
    (
      historicalDataEntries: HistoricalDataEntry[],
      realtimeDataEntries: TelemetryMessage[],
      rawValue?: number
    ): HistoricalDataEntry[] => {
      const historicalData: HistoricalDataEntry[] = historicalDataEntries ?? []

      const realtimeData = realtimeDataEntries.map((e) => {
        const isPumpType = this.TelemetryType === TelemetryType.PumpControl
        const isFlowType = this.TelemetryType === TelemetryType.GasFlow
        const isPressureType =
          this.TelemetryType === TelemetryType.CasingPressure ||
          this.TelemetryType === TelemetryType.TubingPressure

        const data = {
          timestamp: e.CreationTimeUtc.valueOf(),
          value: isFlowType ? e.Payload.value.volumeFlowRate : e.Payload.value,
          rawValue: e.Payload.rawValue,
          unitOfMeasure: isPressureType ? e.Payload.unitOfMeasure : '',
          metaData: {},
          status: '',
        }

        if (isPumpType) {
          data.metaData = e.Payload.metaData
          data.status = e.Payload.value === 1 ? 'ON' : 'OFF'
        }

        return data
      })

      if (this.TelemetryType === TelemetryType.GasFlow) {
        const historicalDataGasFlow: HistoricalDataEntry[] = []
        historicalDataEntries.forEach((element) => {
          const histData = {
            timestamp: element.timestamp,
            value: parseFloat(element.value?.VolumeFlowRate.toString()) || 0,
            todayVolumeFlowed:
              parseFloat(element.value?.TodayVolumeFlowed.toString()) || 0,
            yesterdayVolumeFlowed:
              parseFloat(element.value?.YesterdayVolumeFlowed.toString()) || 0,
            staticPressure:
              parseFloat(element.value?.StaticPressure.toString()) || 0,
            values: element.value,
          }
          historicalDataGasFlow.push(histData)
        })

        const data = historicalDataGasFlow
          .concat(realtimeData)
          .map(this.processData)
          .sort((a, b) => a.timestamp - b.timestamp)

        return data
      }

      if (this.TelemetryType === TelemetryType.TankLevel) {
        const {
          site: { tanks },
        } = this.props

        const tank = tanks.find((t) => t.id === this.props.asset.id)

        if (tank?.productionCode === ProductionCode.Chemical) {
          const data = historicalData
            .concat(realtimeData)
            .map((d) => {
              const value = d.value ?? 0

              const volume = tank ? value : 0

              return this.processData({
                ...d,
                value: volume,
              })
            })
            .sort((a, b) => a.timestamp - b.timestamp)

          return data
        }
      }

      const data = historicalData
        .concat(realtimeData)
        .map(this.processData)
        .sort((a, b) => a.timestamp - b.timestamp)

      return data
    }
  )

  protected readonly memoizesThresholds = memoize(
    (thresholds: StatusRangeResponse[]): StatusRangeResponse[] => {
      return this.processThresholds(thresholds)
    }
  )

  constructor(
    props: AllProps,
    telemetryType: TelemetryType,
    title: string,
    yAxisDomain?: [AxisDomain, AxisDomain]
  ) {
    super(props)

    this.TelemetryType = telemetryType
    this.Title = title
    this.YAxisDomain = yAxisDomain

    this.state = {
      gaugeReading: {
        formattedValue: this.formatValue(),
        formattedRawValue: this.getRawValueFormatByType(),
      },
      isTableViewSelected: false,
      isChartViewSelected: true,
      isChartRateViewSelected: false,
      tankBalance: undefined,
      hiddenData: [],
    }
    previousStateProp = []
    this.updateState = this.updateState.bind(this)
  }

  public getHiddenData(historicalData): string[] {
    const currentTelemetry = Number(Object.keys(historicalData))
    if (historicalData[currentTelemetry] !== undefined) {
      const invalidIds = historicalData[currentTelemetry]
        .filter((x) => x.invalidData)
        .map((x) => x.id)
      return invalidIds
    }
    return []
  }

  public updateState = (id: string, validData: boolean): string[] => {
    let currentState: string[] = this.state.hiddenData

    currentState = currentState.reduce(function (acc: string[], curr) {
      if (!acc.includes(curr)) acc.push(curr)
      return acc
    }, [])

    if (validData) {
      const itemFound = this.state.hiddenData.findIndex((t) => t === id)
      if (itemFound !== undefined && itemFound !== -1) {
        currentState = currentState.filter((x) => x !== id)
      }
      currentState.push(id)

      this.setState((prevState) => ({
        ...prevState,
        hiddenData: currentState,
      }))
    } else {
      const itemFound = this.state.hiddenData.findIndex((t) => t === id)
      if (itemFound !== undefined && itemFound !== -1) {
        currentState = currentState.filter((x) => x !== id)
      }
      this.setState((prevState) => ({
        ...prevState,
        hiddenData: currentState,
      }))
    }
    return currentState
  }

  public async componentDidMount() {
    await this.load()
    await this.loadReferences()
  }

  public render() {
    const { gaugeReading } = this.state
    const content = this.renderContent(
      gaugeReading,
      this.props.initialLatestValue
    )
    return (
      <Sensor
        {...this.state}
        title={this.Title}
        subTitle={this.props.asset.name}
        value={gaugeReading?.formattedValue}
        timestamp={gaugeReading?.timestamp}
        content={content}
        titleExtension={this.getTitleExtension()}
        titleBarContent={this.getTitleContent()}
        {...this.props}
      >
        {this.getDataView()}
      </Sensor>
    )
  }

  protected getTitleExtension(): React.ReactNode | undefined {
    return undefined
  }

  private getDataView(): React.ReactNode {
    const { fullWidthChart, historicalData, realtimeData } = this.props
    const { rawValue } = this.state
    const hiddenHistoricalData = this.hideHistoricalData(
      historicalData,
      this.state.hiddenData
    )

    let renderView: any

    if (this.state.isTableViewSelected) renderView = this.renderTableView
    if (this.state.isChartRateViewSelected)
      renderView = this.renderChartRatesView
    if (this.state.isChartViewSelected) renderView = this.renderChartView

    return renderView.bind(this)(
      hiddenHistoricalData,
      realtimeData,
      rawValue,
      fullWidthChart
    )
  }

  private hideHistoricalData(
    allHistoricalData: HistoricalDataByTelemetryType,
    hiddenData: string[]
  ): HistoricalDataByTelemetryType {
    const currentTelemetry = Number(Object.keys(allHistoricalData))

    if (hiddenData !== undefined && hiddenData.length === 0) {
      const invalidData = this.getHiddenData(allHistoricalData)
      if (previousStateProp.length === 0 && invalidData.length !== 0) {
        hiddenData = invalidData
        this.setState((prevState) => ({
          ...prevState,
          hiddenData: invalidData,
        }))
      }
    }

    if (
      allHistoricalData !== undefined &&
      allHistoricalData[currentTelemetry] !== undefined
    ) {
      allHistoricalData[currentTelemetry].forEach((element) => {
        if (hiddenData !== undefined) {
          if (hiddenData.some((x) => x === element.id)) {
            element.invalidData = true
          } else {
            element.invalidData = false
          }
        }
      })
    }
    return allHistoricalData
  }

  private renderChartView(
    historicalData: HistoricalDataByTelemetryType,
    realtimeData: TelemetryMessage[],
    rawValue?: number,
    fullWidthChart?: boolean
  ) {
    const currRealtime = this.filterByTelemetryTypeAndAssetId(
      realtimeData,
      this.TelemetryType,
      this.props.asset.id
    )
    const data =
      this.memoizedData(
        historicalData[this.TelemetryType]!,
        currRealtime,
        rawValue
      ) ?? []
    const { timeRange, thresholds } = this.props
    const transformedThresholds: StatusRangeResponse[] = thresholds.map(
      (item) => StatusRangeResponse.fromJS(item)
    )

    const isTankChemical = this.props.site?.tanks.some(
      (tank) =>
        tank.id === this.props.asset.id &&
        tank.productionCode === ProductionCode.Chemical
    )

    const statusRangesWithAdjustedUnits = isTankChemical
      ? transformedThresholds
      : this.processStatusRanges(
          transformedThresholds,
          ThresholdsType.Telemetry
        )

    const enhancedThresholds = this.calculateThresholds(
      data,
      statusRangesWithAdjustedUnits,
      ThresholdsType.Telemetry
    )
    const { references, referenceName } = this.state
    return (
      <SensorChart
        {...this.state}
        data={data}
        site={this.props.site}
        historicalData={historicalData}
        formatValue={this.formatValue}
        formatRawValue={this.getRawValueFormatByType}
        yAxisDomain={this.YAxisDomain}
        timeRange={timeRange}
        statusRanges={enhancedThresholds}
        fullWidth={fullWidthChart}
        references={references}
        referenceName={referenceName}
        updateState={this.updateState}
        hideData={this.state.hiddenData}
        thresholdsType={ThresholdsType.Telemetry}
      />
    )
  }

  private processStatusRanges(
    statusRanges: StatusRangeResponse[],
    thresholdsType: ThresholdsType
  ): StatusRangeResponse[] {
    const type = this.TelemetryType

    if (
      type === TelemetryType.TankLevel ||
      type === TelemetryType.BalancedTank ||
      type === TelemetryType.HeaterTemp
    ) {
      const processFunc = this.getProcessFunc(type)

      return statusRanges.map((item) =>
        StatusRangeResponse.fromJS({
          status: item.status,
          thresholdsType: thresholdsType,
          minThreshold: processFunc!(item.minThreshold),
          maxThreshold: processFunc!(item.maxThreshold),
        })
      )
    } else {
      return statusRanges
    }
  }

  private getProcessFunc(telemetryType: TelemetryType) {
    switch (telemetryType) {
      case TelemetryType.TankLevel:
      case TelemetryType.BalancedTank:
        return inchToFeet

      case TelemetryType.HeaterTemp:
        return celsiusToFahrenheit

      default:
        return undefined
    }
  }

  private renderChartRatesView(
    historicalData: HistoricalDataByTelemetryType,
    realtimeData: TelemetryMessage[],
    rawValue?: number,
    fullWidthChart?: boolean
  ) {
    const currRealtime = this.filterByTelemetryTypeAndAssetId(
      realtimeData,
      this.TelemetryType,
      this.props.asset.id
    )
    const data =
      this.memoizedData(
        historicalData[this.TelemetryType]!,
        currRealtime,
        rawValue
      ) ?? []
    const { timeRange, thresholds } = this.props
    const transformedThresholds: StatusRangeResponse[] = thresholds.map(
      (item) => StatusRangeResponse.fromJS(item)
    )
    const enhancedThresholds = this.calculateThresholds(
      data,
      transformedThresholds,
      ThresholdsType.RateOfChange
    )
    const { references, referenceName } = this.state

    return (
      <SensorChartRates
        {...this.state}
        data={data}
        site={this.props.site}
        historicalData={historicalData}
        formatValue={this.formatValue}
        formatRawValue={this.getRawValueFormatByType}
        yAxisDomain={this.YAxisDomain}
        timeRange={timeRange}
        statusRanges={enhancedThresholds}
        fullWidth={fullWidthChart}
        references={references}
        referenceName={referenceName}
        updateState={this.updateState}
        hideData={this.state.hiddenData}
        asset={this.props.asset}
        thresholdsType={ThresholdsType.RateOfChange}
      />
    )
  }

  private filterByTelemetryTypeAndAssetId(
    data: TelemetryMessage[],
    telType: TelemetryType,
    assetId: string
  ) {
    return data.filter(
      (r) => r.TelemetryType === telType && r.AssetId === assetId
    )
  }

  private renderTableView(
    historicalData: HistoricalDataByTelemetryType,
    realtimeData: TelemetryMessage[],
    rawValue?: number,
    fullWidthChart?: boolean
  ) {
    const histAndRealtimeByTelType: HistoricalDataEntry[] = Object.entries(
      historicalData
    ).reduce((data, [key, value]) => {
      const currRealtime = this.filterByTelemetryTypeAndAssetId(
        realtimeData,
        Number(key),
        this.props.asset.id
      )

      return {
        ...data,
        [key]: this.memoizedData(
          historicalData[Number(key)]!,
          currRealtime,
          rawValue
        ),
      }
    }, [])

    return (
      <MeasuresTable
        allHistoricalData={histAndRealtimeByTelType}
        hideData={this.state.hiddenData}
        fullWidth={fullWidthChart}
        loading={this.props.loading}
        updateState={this.updateState}
        site={this.props.site}
        asset={this.props.asset}
        {...this.state}
      />
    )
  }

  private referencesLoaded = false

  public componentDidUpdate(prevProps: SensorWithChartProps, prevState) {
    previousStateProp = prevState.hiddenData

    if (
      this.props.site.id !== prevProps.site.id ||
      this.props.timeRange !== prevProps.timeRange
    ) {
      this.load()
    } else if (
      this.TelemetryType === TelemetryType.LiquidFlow &&
      !this.referencesLoaded
    ) {
      this.loadReferences()
      this.referencesLoaded = true
    }

    const { realtimeData, initialLatestValue } = this.props

    if (this.state.gaugeReading.value === undefined && initialLatestValue) {
      // set value on initial load
      let initialLatestValueHistoryEntry
      if (this.TelemetryType === TelemetryType.GasFlow) {
        initialLatestValueHistoryEntry = {
          timestamp: initialLatestValue.CreationTimeUtc.valueOf(),
          value: initialLatestValue.Payload?.value?.volumeFlowRate || 0,
        }
      } else {
        initialLatestValueHistoryEntry = {
          timestamp: initialLatestValue.CreationTimeUtc.valueOf(),
          value: initialLatestValue.Payload?.value,
          unitOfMeasure: initialLatestValue.Payload?.unitOfMeasure,
        }
      }
      const mostRecentReading = this.getMostRecentReading(
        initialLatestValueHistoryEntry
      )
      this.setState((prevState) => ({
        ...prevState,
        gaugeReading: mostRecentReading,
      }))
      return
    }

    const hasNewRealtimeData =
      realtimeData.length !== prevProps.realtimeData.length

    if (hasNewRealtimeData) {
      if (realtimeData.length === 0) {
        this.setState((prevState) => ({
          ...prevState,
          gaugeReading: {
            formattedValue: this.formatValue(),
            formattedRawValue: this.getRawValueFormatByType(),
          },
        }))
        return
      }

      const flowRealtimeData = realtimeData.filter(
        (m) => m.TelemetryType === this.TelemetryType
      )
      const newRealtimeData = flowRealtimeData.pop()

      if (
        newRealtimeData &&
        this.TelemetryType === newRealtimeData.TelemetryType
      ) {
        const newRealtimeDataHistoryEntry = {
          timestamp: newRealtimeData.CreationTimeUtc.valueOf(),
          value: newRealtimeData.Payload.value,
          rawValue: newRealtimeData.Payload.rawValue,
        }
        const mostRecentReading = this.getMostRecentReading(
          newRealtimeDataHistoryEntry
        )

        this.setState((prevState) => ({
          ...prevState,
          gaugeReading: mostRecentReading,
        }))
        return // eslint-disable-line no-useless-return
      }
    }
  }

  protected abstract renderContent(
    reading: Reading,
    latestTelemetry?: TelemetryMessage
  ): React.ReactNode

  protected formatValue(value?: any, unitOfMeasure?: any): React.ReactNode {
    return (
      <b>
        {typeof value === 'number'
          ? `${value.toFixed(1)} ${unitOfMeasure}`
          : 'N/A'}
      </b>
    )
  }

  protected formatRawValue = (value?: any): React.ReactNode => {
    return (
      <b>
        {typeof value === 'number' ? `${value.toFixed(0)} (Raw Value)` : 'N/A'}
      </b>
    )
  }

  private readonly getRawValueFormatByType = (value?: any) => {
    switch (this.TelemetryType) {
      case TelemetryType.GasFlow:
      case TelemetryType.Flow:
        return undefined

      default:
        return this.formatRawValue(value)
    }
  }

  protected processData(data: HistoricalDataEntry): HistoricalDataEntry {
    return data
  }

  protected async load() {
    await this.loadMostRecentValue()
    await this.loadReferences()
    await this.loadTankBalance()
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected async loadReferences(refTimeRange?: TimeRange) {}

  protected processThresholds(
    thresholds: StatusRangeResponse[]
  ): StatusRangeResponse[] {
    return thresholds.map((t) => StatusRangeResponse.fromJS(t))
  }

  private async loadTankBalance() {
    const { accessToken, tenantId, site, asset } = this.props
    let result

    if (this.TelemetryType === TelemetryType.TankLevel) {
      result = await getTankLevelBalance(
        accessToken,
        tenantId.toString(),
        site.id,
        asset.id
      )
      this.setState((prevState) => ({
        ...prevState,
        tankBalance: result,
      }))
    }
  }

  private async loadMostRecentValue() {
    const { accessToken, tenantId } = this.props

    try {
      let result

      if (this.TelemetryType === TelemetryType.GasFlow) {
        result = await new TelemetryClient(
          multitenantApiBasePath,
          getFetch(accessToken, tenantId.toString())
        ).getLatestTelemetryValuesGET(
          this.TelemetryType,
          this.props.site.id,
          this.props.asset.id
        )
      } else {
        result = await new TelemetryClient(
          multitenantApiBasePath,
          getFetch(accessToken, tenantId.toString())
        ).getLatestTelemetryValuesGET(
          this.TelemetryType,
          this.props.site.id,
          null,
          this.props.asset.id
        )
      }

      const mostRecentValue: Reading = {
        timestamp: result.creationTimeUtc,
        value:
          this.TelemetryType === TelemetryType.GasFlow
            ? result.payload?.value?.volumeFlowRate
            : result.payload?.value,
        rawValue: result.result?.payload?.rawValue,
        formattedValue: this.formatValue(
          this.TelemetryType === TelemetryType.GasFlow
            ? result.payload?.value?.volumeFlowRate
            : result.payload?.value
        ),
        formattedRawValue: this.getRawValueFormatByType(
          result.result?.payload?.rawValue
        ),
        unitOfMeasure: result.result?.payload.unitOfMeasure,
      }

      this.setState((prevState) => ({
        ...prevState,
        gaugeReading: mostRecentValue,
        rawValue: result.result?.payload?.rawValue,
      }))
    } catch (error) {
      console.warn(
        `Failed to load most recent value for telemetry ${
          TelemetryType[this.TelemetryType]
        }`,
        error
      )

      this.setState((prevState) => ({
        ...prevState,
        gaugeReading: {
          formattedValue: this.formatValue(),
          formattedRawValue: this.getRawValueFormatByType(),
        },
      }))
    }
  }

  private getMostRecentReading(mostRecentValue?: HistoricalDataEntry): Reading {
    if (mostRecentValue) {
      const { gaugeReading: prevMostRecentValue } = this.state

      const newMostRecentValue = (value: HistoricalDataEntry) => ({
        value: value.value,
        formattedValue: this.formatValue(value.value, value.unitOfMeasure),
        formattedRawValue: this.getRawValueFormatByType(),
        timestamp: value.timestamp ? new Date(value.timestamp) : undefined,
        unitOfMeasure: value.unitOfMeasure,
      })

      return mostRecentValue.timestamp >
        (prevMostRecentValue.timestamp?.valueOf() ?? 0)
        ? newMostRecentValue(mostRecentValue)
        : prevMostRecentValue
    } else {
      return { formattedValue: 'N/A', formattedRawValue: 'N/A' }
    }
  }

  private readonly getTitleContent = (): React.ReactNode => {
    const getTelemetryTypeFormula = (asset: any) => {
      return asset.telemetryTypeFormula
    }

    const renderAssetInfoButton =
      getTelemetryTypeFormula(this.props.asset) &&
      this.TelemetryType !== TelemetryType.FlareStatus &&
      this.TelemetryType !== TelemetryType.PumpControl

    const renderViewButton = this.TelemetryType !== TelemetryType.FlareStatus
    const isTank = this.TelemetryType === TelemetryType.TankLevel
    const isTankChemical =
      isTank &&
      this.props.site?.tanks.some(
        (tank) =>
          tank.id === this.props.asset.id &&
          tank.productionCode === ProductionCode.Chemical
      )

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', gap: '8px' }}>
          {renderAssetInfoButton && this.getAssetInfo()}
          {this.TelemetryType !== TelemetryType.Vibration &&
            this.TelemetryType !== TelemetryType.PumpControl &&
            this.TelemetryType !== TelemetryType.GasFlow &&
            this.getThresholds()}
          {isTankChemical && this.getRateOfChangeThresholds()}
        </div>

        <div>{renderViewButton && this.getViewButtons()}</div>
      </div>
    )
  }

  private readonly getAssetInfo = (): React.ReactNode => {
    const { onOpenAssetInfo } = this.props

    if (!onOpenAssetInfo) {
      return null
    }

    return (
      <Tooltip title="Show asset information">
        <IconButton
          onClick={() =>
            onOpenAssetInfo(
              this.TelemetryType,
              this.props.asset,
              this.props.site
            )
          }
          size="small"
        >
          <Info />
        </IconButton>
      </Tooltip>
    )
  }

  private readonly getThresholds = (): React.ReactNode => {
    if (this.state.loading) {
      return (
        <LinearProgress
          style={{
            width: '100%',
            marginLeft: theme.spacing(1),
          }}
        />
      )
    }

    const { onOpenThresholds } = this.props

    if (!onOpenThresholds) {
      return null
    }

    return (
      <Tooltip title="Telemetry Thresholds">
        <IconButton
          onClick={() =>
            onOpenThresholds(
              this.TelemetryType,
              this.props.asset.id,
              this.props.asset.name ?? this.props.asset.id
            )
          }
          size="small"
        >
          <TuneTwoTone />
        </IconButton>
      </Tooltip>
    )
  }

  private readonly getRateOfChangeThresholds = (): React.ReactNode => {
    if (this.state.loading) {
      return (
        <LinearProgress
          style={{
            width: '100%',
            marginLeft: theme.spacing(1),
          }}
        />
      )
    }

    const { onOpenRateOfChangeThresholds } = this.props
    if (!onOpenRateOfChangeThresholds) {
      return null
    }
    return (
      <Tooltip title="Rate of change thresholds">
        <IconButton
          onClick={() =>
            onOpenRateOfChangeThresholds(
              TelemetryType.TankLevel,
              this.props.asset.id,
              this.props.asset.name ?? this.props.asset.id
            )
          }
          size="small"
        >
          <ChromeReaderModeTwoTone />
        </IconButton>
      </Tooltip>
    )
  }

  private readonly getViewButtons = (): React.ReactNode => {
    const {
      isTableViewSelected,
      isChartRateViewSelected,
      isChartViewSelected,
    } = this.state
    const noShowChart =
      this.TelemetryType !== TelemetryType.Vibration &&
      this.TelemetryType !== TelemetryType.PumpControl &&
      this.TelemetryType !== TelemetryType.FlareStatus &&
      this.TelemetryType !== TelemetryType.GasFlow

    const noChart = this.TelemetryType !== TelemetryType.PumpControl
    return (
      <>
        {noChart && this.getViewChartButton(isChartViewSelected)}
        {this.getViewTableButton(isTableViewSelected)}
        {noShowChart && this.getViewChartRateButton(isChartRateViewSelected)}
      </>
    )
  }

  private readonly getViewTableButton = (
    isSelected: boolean
  ): React.ReactNode => {
    return (
      <Tooltip title="Switch to table view">
        <span>
          <IconButton
            onClick={() =>
              this.setState({
                isTableViewSelected: true,
                isChartRateViewSelected: false,
                isChartViewSelected: false,
              })
            }
            size="small"
            disabled={isSelected}
            style={{
              border: isSelected ? '1px solid #F27A54' : 'transparent',
              borderRadius: '4px',
            }}
          >
            <TableChartTwoTone />
          </IconButton>
        </span>
      </Tooltip>
    )
  }

  private readonly getViewChartButton = (
    isSelected: boolean
  ): React.ReactNode => {
    return (
      <Tooltip title="Switch to chart view">
        <span>
          <IconButton
            onClick={() =>
              this.setState({
                isTableViewSelected: false,
                isChartRateViewSelected: false,
                isChartViewSelected: true,
              })
            }
            size="small"
            disabled={isSelected}
            style={{
              border: isSelected ? '1px solid #F27A54' : 'transparent',
              borderRadius: '4px',
            }}
          >
            <TimelineTwoTone />
          </IconButton>
        </span>
      </Tooltip>
    )
  }

  private readonly getViewChartRateButton = (
    isSelected: boolean
  ): React.ReactNode => {
    return (
      <Tooltip title="Switch to chart rate view">
        <span>
          <IconButton
            onClick={() =>
              this.setState({
                isTableViewSelected: false,
                isChartRateViewSelected: true,
                isChartViewSelected: false,
              })
            }
            size="small"
            disabled={isSelected}
            style={{
              border: isSelected ? '1px solid #F27A54' : 'transparent',
              borderRadius: '4px',
            }}
          >
            <TrendingUpTwoTone />
          </IconButton>
        </span>
      </Tooltip>
    )
  }

  private readonly calculateThresholds = (
    data: HistoricalDataEntry[],
    statusThresholds: StatusRangeResponse[],
    thresholdsType: ThresholdsType
  ): StatusRangeResponse[] => {
    const statusesToShow = [
      TelemetryStatus.Yellow,
      TelemetryStatus.Red,
      TelemetryStatus.Green,
    ]
    const thresholdViewModels = statusThresholds.filter(
      (th) =>
        statusesToShow.includes(th.status) &&
        th.thresholdsType === thresholdsType
    )

    if (data?.length && thresholdViewModels?.length) {
      const extraRange = 20

      const maxThresholdValue = thresholdViewModels.reduce(
        (to, current) => Math.max(to, current.maxThreshold),
        thresholdViewModels[0].maxThreshold
      )

      const maxDataValue = data.reduce(
        (max, current) => Math.max(max, current.value),
        data[0].value as number
      )

      if (maxDataValue > maxThresholdValue) {
        const threshold = {
          ...thresholdViewModels[0],
          status: TelemetryStatus.Red,
          minThreshold: maxThresholdValue,
          maxThreshold: maxDataValue + extraRange,
        }
        thresholdViewModels.push(threshold)
      }

      const minThresholdValue = thresholdViewModels.reduce(
        (from, current) => Math.min(from, current.minThreshold),
        thresholdViewModels[0].minThreshold
      )

      const minDataValue = data.reduce(
        (min, current) => Math.min(min, current.value),
        data[0].value
      )

      if (minDataValue < minThresholdValue) {
        const threshold = {
          ...thresholdViewModels[0],
          status: TelemetryStatus.Red,
          minThreshold: minDataValue - extraRange,
          maxThreshold: minThresholdValue,
        }
        thresholdViewModels.push(threshold)
      }
    }

    return thresholdViewModels
  }
}
